import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, ReferenceLine, Dot, Tooltip } from 'recharts';
import { FileText, Calendar, TrendingUp, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { useTranslation } from "react-i18next";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const ReportStatusChart = () => {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState([]);
  const [peakValue, setPeakValue] = useState(null);
  const [peakDate, setPeakDate] = useState(null);
  const [peakType, setPeakType] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch report data from backend
  useEffect(() => {
    const fetchReportData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${API_BASE_URL}/api/reports/monthly-status`);
        if (!response.ok) throw new Error(t('recentlyEditedReports.error'));
        const result = await response.json();
        setData(result);
        // Find peak value across all metrics
        let maxVal = 0;
        let maxDate = '';
        let maxType = '';
        result.forEach(item => {
          if (item.resolved > maxVal) {
            maxVal = item.resolved;
            maxDate = item.month;
            maxType = 'resolved';
          }
          if (item.pending > maxVal) {
            maxVal = item.pending;
            maxDate = item.month;
            maxType = 'pending';
          }
          if (item.dismissed > maxVal) {
            maxVal = item.dismissed;
            maxDate = item.month;
            maxType = 'dismissed';
          }
        });
        setPeakValue(maxVal);
        setPeakDate(maxDate);
        setPeakType(maxType);
      } catch (err) {
        setError(err.message || t('recentlyEditedReports.error'));
      } finally {
        setLoading(false);
      }
    };
    fetchReportData();
  }, [t, i18n.language]);

  const CustomDot = (props) => {
    const { cx, cy, payload, dataKey } = props;
    if (payload.month === peakDate && dataKey === peakType) {
      const color = dataKey === 'resolved' ? '#22c55e' : dataKey === 'pending' ? '#f59e0b' : '#ef4444';
      return (
        <g>
          <circle 
            cx={cx} 
            cy={cy} 
            r={6} 
            fill={color} 
            stroke="#ffffff" 
            strokeWidth={3}
            className="animate-pulse"
          />
          <circle 
            cx={cx} 
            cy={cy} 
            r={12} 
            fill="none" 
            stroke={color} 
            strokeWidth={2}
            opacity={0.3}
            className="animate-ping"
          />
        </g>
      );
    }
    return null;
  };

  // Custom Tooltip for monthly breakdown
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white/90 backdrop-blur-md rounded-xl shadow-lg border border-blue-100 p-2 sm:p-4 min-w-[110px] sm:min-w-[180px] text-xs sm:text-base">
          <div className="font-semibold text-blue-700 mb-1 sm:mb-2">{label} 2024</div>
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <span className="inline-block w-3 h-3 rounded-full bg-[#f59e0b]"></span>
              <span className="text-blue-700 font-medium">{t('recentlyEditedReports.status.resolved')}</span>
              <span className="ml-auto text-blue-900 font-bold">{data.resolved}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-block w-3 h-3 rounded-full bg-[#000000]"></span>
              <span className="text-purple-700 font-medium">{t('recentlyEditedReports.status.pending')}</span>
              <span className="ml-auto text-purple-900 font-bold">{data.pending}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-block w-3 h-3 rounded-full bg-[#ea580c]"></span>
              <span className="text-pink-700 font-medium">{t('recentlyEditedReports.status.dismissed')}</span>
              <span className="ml-auto text-pink-900 font-bold">{data.dismissed}</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Update getStatusColor to use new palette
  const getStatusColor = (type) => {
    switch(type) {
      case 'resolved': return '#f59e0b'; // Orange (Amber-500)
      case 'pending': return '#000000'; // Black
      case 'dismissed': return '#ea580c'; // Deep Orange (Orange-600)
      default: return '#6b7280';
    }
  };

  // Update getStatusIcon to match new colors
  const getStatusIcon = (type) => {
    switch(type) {
      case 'resolved': return <CheckCircle className="w-4 h-4 text-[#f59e0b]" />;
      case 'pending': return <AlertCircle className="w-4 h-4 text-[#000000]" />;
      case 'dismissed': return <XCircle className="w-4 h-4 text-[#ea580c]" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const getStatusLabel = (type) => {
    switch(type) {
      case 'resolved': return t('recentlyEditedReports.status.resolved');
      case 'pending': return t('recentlyEditedReports.status.pending');
      case 'dismissed': return t('recentlyEditedReports.status.dismissed');
      default: return t('recentlyEditedReports.status');
    }
  };

  const calculateTotal = () => {
    return data.reduce((acc, item) => acc + item.resolved + item.pending + item.dismissed, 0);
  };

  const calculateAverage = (type) => {
    if (data.length === 0) return 0;
    return Math.round(data.reduce((acc, item) => acc + item[type], 0) / data.length);
  };

  if (loading) {
    return (
      <div className="w-full max-w-6xl mx-auto p-2 sm:p-4 md:p-6">
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 p-4 sm:p-8">
          <div className="flex items-center justify-center h-64 sm:h-80 md:h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">{t('chart.loading')}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full max-w-6xl mx-auto p-2 sm:p-4 md:p-6">
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 p-4 sm:p-8">
          <div className="flex items-center justify-center h-64 sm:h-80 md:h-96">
            <div className="text-center">
              <div className="text-red-500 mb-4">{error}</div>
              <p className="text-gray-600">{t('chart.error')}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="w-full"
      dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
      style={{ fontFamily: i18n.language === 'ar' ? "'Amiri', serif" : 'inherit' }}
    >
      {/* Chart Container */}
      <div className="py-2 sm:py-4 relative overflow-hidden rounded-2xl border border-slate-200 shadow-lg bg-white w-full max-w-none mx-auto">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute inset-0" />
          <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#c7d2fe" strokeWidth="0.5"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        {/* Peak Value Annotation */}
        <div className={`absolute top-2 sm:top-4 left-1/2 transform -translate-x-1/2 z-10 w-full max-w-xs sm:max-w-lg sm:w-auto px-1 sm:px-4 ${i18n.language === 'ar' ? 'text-right' : ''}`}
          style={{ pointerEvents: 'none' }}>
          <div 
            className="text-black px-1 sm:px-4 py-1 sm:py-2 rounded-full text-xs sm:text-sm font-medium flex items-center space-x-1 sm:space-x-2 shadow-lg bg-gray-100"
            style={{ fontFamily: i18n.language === 'ar' ? "'Amiri', serif" : 'inherit' }}
          >
            <Calendar className="w-4 h-4" />
            <span className='text-black'>{peakDate} 2024</span>
            {getStatusIcon(peakType)}
            <span className='text-black'>{peakValue} {getStatusLabel(peakType)}</span>
          </div>
        </div>

        {/* Chart */}
        <div className="relative z-10 h-[220px] sm:h-[320px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 60, right: 10, left: 0, bottom: 20 }}>
              <defs>
                <linearGradient id="resolvedGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#facc15" stopOpacity={0.1}/>
                </linearGradient>
                <linearGradient id="pendingGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#000000" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#282c34" stopOpacity={0.1}/>
                </linearGradient>
                <linearGradient id="dismissedGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ea580c" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke="#e5e7eb" 
                opacity={0.3}
                horizontal={true}
                vertical={false}
              />
              
              <XAxis 
                dataKey="month" 
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#9CA3AF', fontSize: 13, fontWeight: 500 }}
                dy={10}
              />
              
              <YAxis 
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#9CA3AF', fontSize: 13, fontWeight: 500 }}
                domain={[0, 'dataMax + 10']}
                dx={-10}
              />
              
              {/* Peak reference line */}
              <ReferenceLine 
                y={peakValue} 
                stroke={getStatusColor(peakType)} 
                strokeDasharray="5 5" 
                strokeWidth={2}
                opacity={0.5}
              />
              
              <Line 
                type="monotone" 
                dataKey="resolved" 
                stroke="#f59e0b"
                strokeWidth={3}
                dot={<CustomDot />}
                activeDot={{ r: 6, fill: '#f59e0b', stroke: '#ffffff', strokeWidth: 3 }}
                filter="url(#dropshadow)"
              />
              
              <Line 
                type="monotone" 
                dataKey="pending" 
                stroke="#000000"
                strokeWidth={3}
                dot={<CustomDot />}
                activeDot={{ r: 6, fill: '#000000', stroke: '#ffffff', strokeWidth: 3 }}
                filter="url(#dropshadow)"
              />
              
              <Line 
                type="monotone" 
                dataKey="dismissed" 
                stroke="#ea580c"
                strokeWidth={3}
                dot={<CustomDot />}
                activeDot={{ r: 6, fill: '#ea580c', stroke: '#ffffff', strokeWidth: 3 }}
                filter="url(#dropshadow)"
              />
              
              {/* Add subtle drop shadow filter */}
              <defs>
                <filter id="dropshadow" x="-50%" y="-50%" width="200%" height="200%">
                  <feDropShadow dx="0" dy="2" stdDeviation="2" floodColor="#000000" floodOpacity="0.1"/>
                </filter>
              </defs>
              <Tooltip content={<CustomTooltip />} cursor={{ fill: '#e0e7ff', opacity: 0.15 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Legend */}
        <div className={`flex flex-wrap flex-col xs:flex-row sm:flex-row items-center justify-center gap-2 xs:gap-4 sm:space-x-8 mt-2 sm:mt-6 ${i18n.language === 'ar' ? 'text-right' : ''}`}
          style={{ fontFamily: i18n.language === 'ar' ? "'Amiri', serif" : 'inherit', fontSize: '0.85rem' }}>
          <div className="flex items-center space-x-2 mx-2 mb-1 xs:mb-0">
            <div className="w-4 h-1 rounded-full" style={{background:'#f59e0b'}}></div>
            <span className="text-xs sm:text-sm font-medium mx-2" style={{color:'#f59e0b'}}>{t('recentlyEditedReports.status.resolved')}</span>
          </div>
          <div className="flex items-center space-x-2 mx-2 mb-1 xs:mb-0">
            <div className="w-4 h-1 rounded-full" style={{background:'#000000'}}></div>
            <span className="text-xs sm:text-sm font-medium mx-2" style={{color:'#000000'}}>{t('recentlyEditedReports.status.pending')}</span>
          </div>
          <div className="flex items-center mx-2 space-x-2">
            <div className="w-4 h-1 rounded-full" style={{background:'#ea580c'}}></div>
            <span className="text-xs sm:text-sm font-medium mx-2" style={{color:'#ea580c'}}>{t('recentlyEditedReports.status.dismissed')}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportStatusChart;